import React from 'react';
import { FiCheckCircle, FiLoader, FiClock, FiAlertCircle, FiPlay, FiCalendar, FiRotateCcw, FiDatabase } from 'react-icons/fi';
import { TooltipTop } from '@components/tooltips';
import { StatusBadge } from '@components/status-badge';
import { StatusBadgeVariant } from '@components/status-badge/types';
import { AirflowDagRunState, AirflowRunType, AirflowTaskInstanceState, DateRange } from './types';

export const EMPTY_RANGE: DateRange = { from: '', to: '' };

/*
 * Renders a dash for null/undefined/empty values, and stringifies booleans, else String(value)
 */
export function displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
}

const TASK_INSTANCE_STATE_VARIANT_MAP: Record<string, StatusBadgeVariant> = {
    success: 'success',
    failed: 'destructive',
    upstream_failed: 'destructive',
    running: 'info',
    queued: 'warning',
    scheduled: 'warning',
    deferred: 'warning',
    up_for_retry: 'warning',
    up_for_reschedule: 'warning',
    skipped: 'secondary',
    removed: 'secondary',
    restarting: 'info',
    none: 'secondary',
};

/*
 * Maps a task instance state to a StatusBadge variant, defaulting to secondary
 */
export function taskInstanceStateVariant(state?: string | null): StatusBadgeVariant {
    if (!state) return 'secondary';
    return TASK_INSTANCE_STATE_VARIANT_MAP[state] ?? 'secondary';
}

const DAG_RUN_STATE_VARIANT_MAP: Record<AirflowDagRunState, StatusBadgeVariant> = {
    success: 'success',
    failed: 'destructive',
    running: 'info',
    queued: 'warning',
};

/*
 * A table header label with an info icon tooltip, used for columns needing extra context
 */
export function HeaderWithTooltip({ label, tip }: { label: string; tip: string }) {
    return (
        <TooltipTop title={tip}>
            <span>{label}</span>
        </TooltipTop>
    );
}

/*
 * Colored status badge for a DAG run's state
 */
export function DagRunStateBadge({ state }: { state: AirflowDagRunState }) {
    return <StatusBadge variant={DAG_RUN_STATE_VARIANT_MAP[state] ?? 'secondary'}>{state}</StatusBadge>;
}

/*
 * Colored status badge for a task instance's state
 */
export function TaskInstanceStateBadge({ state }: { state?: AirflowTaskInstanceState | null }) {
    return <StatusBadge variant={taskInstanceStateVariant(state)}>{state ?? 'none'}</StatusBadge>;
}

/*
 * Formats an ISO timestamp using the browser's locale, or a dash if absent
 */
export function formatDateTime(iso?: string | null): string {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

/*
 * Formats an ISO timestamp as "YYYY-MM-DD, HH:MM:SS UTC"
 */
export function formatDateTimeUtc(iso?: string | null): string {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}, ${pad(date.getUTCHours())}:${pad(
        date.getUTCMinutes()
    )}:${pad(date.getUTCSeconds())} UTC`;
}

/*
 * Formats a millisecond duration as seconds/minutes+seconds/hours+minutes
 */
export function formatDurationMs(ms: number | null | undefined): string {
    if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
    const totalSeconds = Math.round(Math.abs(ms) / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) return `${minutes}m ${seconds}s`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
}

/*
 * Formats a duration given in seconds, delegating to formatDurationMs
 */
export function formatDurationSeconds(seconds?: number | null): string {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
    return formatDurationMs(Math.abs(seconds) * 1000);
}

/*
 * Formats a duration given in seconds as HH:MM:SS.mmm
 */
export function formatClockDuration(seconds?: number | null): string {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
    const abs = Math.abs(seconds);
    const hours = Math.floor(abs / 3600);
    const minutes = Math.floor((abs % 3600) / 60);
    const secs = Math.floor(abs % 60);
    const ms = Math.round((abs - Math.floor(abs)) * 1000);
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}.${pad(ms, 3)}`;
}

/*
 * Icon representing a DAG run's state (success/running/queued/failed)
 */
export function RunStateIcon({ state }: { state: AirflowDagRunState }) {
    switch (state) {
        case 'success':
            return <FiCheckCircle color="#08c46a" />;
        case 'running':
            return <FiLoader color="#1890ff" className="spin" />;
        case 'queued':
            return <FiClock color="#fcad01" />;
        default:
            return <FiAlertCircle color="#ff4d4f" />;
    }
}

const RUN_TYPE_ICON_MAP: Record<AirflowRunType, React.ReactNode> = {
    manual: <FiPlay />,
    scheduled: <FiCalendar />,
    backfill: <FiRotateCcw />,
    asset_triggered: <FiDatabase />,
};

/*
 * Icon + capitalized label describing a DAG run's trigger type
 */
export function RunTypeLabel({ runType }: { runType: AirflowRunType }) {
    const label = runType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return (
        <span className="airflow__runTypeLabel">
            {RUN_TYPE_ICON_MAP[runType] ?? <FiPlay />} {label}
        </span>
    );
}

/*
 * Formats a DAG's version range as "vN" or "vMin–vMax"
 */
export function formatDagVersion(versions?: { version_number: number }[]): string {
    if (!versions || versions.length === 0) return '—';
    const numbers = versions.map((v) => v.version_number);
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    return min === max ? `v${min}` : `v${min}–v${max}`;
}

/*
 * Converts a 5-field cron expression (or @macro) into a human-readable schedule description
 */
export function cronToHuman(expr: string): string {
    if (!expr) return expr;
    if (expr.startsWith('@')) {
        const macroMap: Record<string, string> = {
            '@hourly': 'Every hour',
            '@daily': 'Every day at midnight',
            '@weekly': 'Every week',
            '@monthly': 'Every month',
            '@yearly': 'Every year',
            '@once': 'Once',
        };
        return macroMap[expr] ?? expr;
    }
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return expr;
    const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
    const pad = (n: string) => n.padStart(2, '0');

    if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
        return `Every ${minute.slice(2)} minutes`;
    }
    if (hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
        return 'Every hour';
    }
    if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && dayOfWeek === '*') {
        return `Every day at ${pad(hour)}:${pad(minute)}`;
    }
    const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && /^\d+$/.test(dayOfWeek)) {
        return `Every week on ${WEEKDAYS[Number(dayOfWeek)] ?? dayOfWeek} at ${pad(hour)}:${pad(minute)}`;
    }
    if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(dayOfMonth) && dayOfWeek === '*') {
        return `Every month on day ${dayOfMonth} at ${pad(hour)}:${pad(minute)}`;
    }
    return expr;
}

/*
 * Picks the best available schedule description for a DAG: explicit description,
 * then summary, then a human-readable rendering of the raw cron expression
 */
export function formatSchedule(description?: string | null, summary?: string | null): string {
    if (description) return description;
    if (summary) return cronToHuman(summary);
    return '—';
}

/*
 * Converts a YYYY-MM-DD date string to an ISO timestamp at the start or end of that day (UTC)
 */
export function dayBoundaryIso(date: string, end: boolean): string | undefined {
    if (!date) return undefined;
    return `${date}T${end ? '23:59:59' : '00:00:00'}Z`;
}

/*
 * Builds a {gte, lte} ISO range spanning the last N hours from now
 */
export function hoursAgoRange(hours: number): { gte: string; lte: string } {
    const now = new Date();
    const past = new Date(now.getTime() - hours * 60 * 60 * 1000);
    return { gte: past.toISOString(), lte: now.toISOString() };
}

/*
 * Buckets a list of ISO timestamps into evenly-spaced counts across a time window,
 * for sparkline charts
 */
export function bucketTimestamps(timestamps: (string | null | undefined)[], gteIso: string, lteIso: string, buckets = 12): { index: number; count: number }[] {
    const start = new Date(gteIso).getTime();
    const end = new Date(lteIso).getTime();
    const span = Math.max(end - start, 1);
    const result = Array.from({ length: buckets }, (_, index) => ({ index, count: 0 }));
    timestamps.forEach((ts) => {
        if (!ts) return;
        const time = new Date(ts).getTime();
        if (Number.isNaN(time)) return;
        const ratio = (time - start) / span;
        const bucketIndex = Math.min(buckets - 1, Math.max(0, Math.floor(ratio * buckets)));
        result[bucketIndex].count += 1;
    });
    return result;
}

/*
 * Parses a numeric string, returning undefined for empty/invalid input
 */
export function toNumber(value: string): number | undefined {
    if (value === '') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
}

const EVENT_COLOR_KEYWORDS: { match: RegExp; variant: StatusBadgeVariant }[] = [
    { match: /fail|error/i, variant: 'destructive' },
    { match: /success/i, variant: 'success' },
    { match: /mismatch|warn|retry/i, variant: 'warning' },
    { match: /running|trigger|start|create/i, variant: 'info' },
];

/*
 * Maps an audit-log event name to a StatusBadge variant by keyword, shared by every
 * audit-log tab across the dashboard
 */
export function eventColorClass(event: string): StatusBadgeVariant {
    const match = EVENT_COLOR_KEYWORDS.find((entry) => entry.match.test(event));
    return match?.variant ?? 'secondary';
}
